import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import { fetchCollection, fetchCollections, fetchEthUsdPrice, fetchLaunchpads } from './reducer'
import { State } from '../types'
import { NftActivityFilter, NftFilter, NftFilterLoadingState, NftToken, UserNftsState } from './types'

const MAX_GEN0_ID = 4

export const useFetchCollections = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchCollections())
  }, [dispatch])
}

export const useFetchCollection = (collectionAddress: string) => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchCollection(collectionAddress))
  }, [dispatch, collectionAddress])
}

export const useFetchLaunchpads = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchLaunchpads())
  }, [dispatch])
}

export const useFetchEthUsdPrice = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchEthUsdPrice())
  }, [dispatch])
}

export const useGetCollections = () => {
  return useSelector((state: State) => state.nftMarket.data.collections)
}

export const useGetLaunchpads = () => {
  return useSelector((state: State) => state.nftMarket.data.launchpads)
}

export const useEthUsdPrice = () => {
  return useSelector((state: State) => state.nftMarket.data.ethUsdPrice)
}

export const useGetCollection = (collectionAddress: string) => {
  // const checksummedCollectionAddress = isAddress(collectionAddress) || ''
  const collections = useGetCollections()
  return Object.values(collections).find(
    (v) => v.address.toLowerCase() === collectionAddress.toLowerCase() || v.collectionUID === collectionAddress,
  )
}
export const useGetLaunchpad = (collectionAddress: string) => {
  // const checksummedCollectionAddress = isAddress(collectionAddress) || ''
  const launchpads = useGetLaunchpads()
  return Object.values(launchpads).find((v) => v.address.toLowerCase() === collectionAddress.toLowerCase())
}

export const useNftsFromCollection = (collectionAddress: string) => {
  // const checksummedCollectionAddress = isAddress(collectionAddress) || ''
  const nfts: NftToken[] = useSelector((state: State) => {
    return state.nftMarket.data.nfts[collectionAddress]
  })
  return nfts
}

export const useGetNFTInitializationState = () => {
  return useSelector((state: State) => state.nftMarket.initializationState)
}

export const useUserNfts = (): UserNftsState => {
  return useSelector((state: State) => state.nftMarket.data.user)
}

export const useHasGen0Nfts = (): boolean => {
  const userNfts = useSelector((state: State) => state.nftMarket.data.user)
  return userNfts.nfts.some((nft) => nft.attributes && Number(nft.attributes[0]?.value) <= MAX_GEN0_ID)
}

export const useGetNftFilters = (collectionAddress: string) => {
  const collectionFilter: NftFilter = useSelector((state: State) => state.nftMarket.data.filters[collectionAddress])
  return collectionFilter || {}
}

export const useGetNftActiveFilters = (collectionAddress: string) => {
  const collectionFilter: NftFilter = useSelector((state: State) => state.nftMarket.data.filters[collectionAddress])
  return collectionFilter ? collectionFilter.activeFilters : {}
}

export const useGetNftFilterLoadingState = (collectionAddress: string) => {
  const collectionFilter: NftFilter = useSelector((state: State) => state.nftMarket.data.filters[collectionAddress])
  return collectionFilter ? collectionFilter.loadingState : NftFilterLoadingState.IDLE
}

export const useGetNftOrdering = (collectionAddress: string) => {
  const collectionFilter: NftFilter = useSelector((state: State) => state.nftMarket.data.filters[collectionAddress])
  return collectionFilter ? collectionFilter.ordering : { field: 'tokenId', direction: 'asc' as 'asc' | 'desc' }
  // return collectionFilter ? collectionFilter.ordering : { field: 'currentAskPrice', direction: 'asc' as 'asc' | 'desc' }
}

export const useGetNftShowOnlyOnSale = (collectionAddress: string) => {
  const collectionFilter: NftFilter = useSelector((state: State) => state.nftMarket.data.filters[collectionAddress])
  return collectionFilter ? collectionFilter.showOnlyOnSale : false
}

export const useGetNftActivityFilters = (collectionAddress: string) => {
  const collectionFilter: NftActivityFilter = useSelector(
    (state: State) => state.nftMarket.data.activityFilters[collectionAddress],
  )
  return collectionFilter || { typeFilters: [] }
}
